import Box from '@components/Box';
import ConfirmEmail from '@components/ConfirmEmail';
import PasswordField from '@components/PasswordField';
import TextInputContainer from '@containers/TextInputContainer';
import WrapperDetails from './WrapperDetails';
import { AxiosError } from 'axios';
import { Column, Grid, Row } from '@components/Grid';
import { Modal } from '@components/Modal';
import { TProfileLoginForm } from './../types';
import { updateProfileLogin } from '@api/apiClient';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useProfile } from '@contexts/ProfileContext';
import { useTranslation } from 'react-i18next';

const LoginDetails: React.FC = (): React.ReactElement => {
  const [isOpenConfirmEmailWindow, setIsOpenConfirmEmailWindow] = useState<boolean>(false);

  const { account, get } = useProfile();
  const { t } = useTranslation();

  const {
    error: errorUpdateProfileLogin,
    isError: isErrorUpdateProfileLogin,
    isLoading: isLoadingUpdateProfileLogin,
    mutate: mutateUpdateProfileLogin,
    reset: resetUpdateProfileLogin
  } = useMutation<unknown, AxiosError<any>, TProfileLoginForm>({
    mutationFn: updateProfileLogin,
    onSuccess: (_, variables) => {
      reset(variables);
      !account?.email && setIsOpenConfirmEmailWindow(true);
    }
  });

  const { control, formState, handleSubmit, reset, watch } = useForm<TProfileLoginForm>({
    defaultValues: {
      email: account?.email ?? '',
      oldPassword: '',
      password: ''
    }
  });

  const email = watch('email');
  const oldPassword = watch('oldPassword');
  const password = watch('password');

  const submit = (form: TProfileLoginForm) => {
    const data: TProfileLoginForm = {
      email: form.email,
      oldPassword: account?.email ? form.oldPassword : null,
      password: form.password,
      sourceType: 'Web'
    };
    mutateUpdateProfileLogin(data);
  };

  useEffect(() => {
    resetUpdateProfileLogin();
  }, [oldPassword]);

  return (
      <>
        <WrapperDetails
            btnAction={() => {
              handleSubmit(submit)();
              resetUpdateProfileLogin();
            }}
            btnDisabled={account?.email ? !(formState.dirtyFields.oldPassword && formState.dirtyFields.password) : !(formState.dirtyFields.email && formState.dirtyFields.password)}
            btnPending={isLoadingUpdateProfileLogin}
            btnText={t('profileData.buttonSave')}
            error={errorUpdateProfileLogin}
            errorPrefix="profileData.loginDetails.errors"
            isActionVisible
            isError={isErrorUpdateProfileLogin}
            title={t('profileData.loginData')}
        >
          <Grid>
            <Row>
              <Box pb={5}>
                <TextInputContainer
                    control={control}
                    disabled={!!account?.email}
                    hasError={errorUpdateProfileLogin?.response?.data?.code === 'email-already-registered'}
                    name="email"
                    rules={{ required: t('defaultFormErrors.required') }}
                    title={t('profileData.email')}
                    type="email"
                />
              </Box>
            </Row>
            {account?.email ? (
                <>
                  <Row>
                    <Box pb={5}>
                      <Column>
                        <PasswordField<TProfileLoginForm>
                            calledFrom="profileData"
                            control={control}
                            hasError={errorUpdateProfileLogin?.response?.data?.code === 'invalid-password'}
                            name="oldPassword"
                            password={oldPassword! ?? ''}
                            showPasswordStrength={false}
                        />
                      </Column>
                    </Box>
                  </Row>
                  <PasswordField<TProfileLoginForm>
                      calledFrom="profileData"
                      control={control}
                      name="password"
                      password={password! ?? ''}
                  />
                </>
            ) : (
                <PasswordField<TProfileLoginForm>
                    control={control}
                    password={password! ?? ''}
                />
            )}
          </Grid>
        </WrapperDetails>
        <Modal
            isOpen={isOpenConfirmEmailWindow}
            onClickOverlay={() => setIsOpenConfirmEmailWindow(false)}
            onClose={() => setIsOpenConfirmEmailWindow(false)}
        >
          <ConfirmEmail
              calledFrom="profileData"
              handlerSubmit={handleSubmit(submit)}
              onSuccess={() => {
                get();
                setIsOpenConfirmEmailWindow(false);
              }}
              email={email}
          />
        </Modal>
      </>
  );
};

export default LoginDetails;